// Copyright (c) Docugami, Inc. All rights reserved.

import { withAuthenticationRequired } from '@auth0/auth0-react';
import { initializeIcons } from '@fluentui/font-icons-mdl2/lib';
import { skipToken } from '@reduxjs/toolkit/dist/query/react';
import { UpgradeAccountModal } from 'components/common/LimitsContainer';
import { Config } from 'config';
import dayjs from 'dayjs';
import { useAuthenticateRequests } from 'hooks/AuthenticateRequests';
import { useDgAuth } from 'hooks/DgAuthentication';
import { useFileManagerReduxInterop } from 'hooks/FileManagerReduxInterop';
import { DEVELOPER_HUB_PAGE, DOC_SHELL_PAGE, MY_CONTENT_PAGE, NavigationType, WORK_PATH_PAGE, useNavigationService } from 'hooks/navigationService';
import { useNetworkErrorTracker } from 'hooks/networkErrorTracker';
import { useOrgStatus } from 'hooks/OrgStatus';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { useTelemetryContext, useTelemetryUserContext } from 'hooks/TelemetryContext';
import { useUploadProgress } from 'hooks/UploadProgress';
import { stopSignalRConnection } from 'networking/OrganizationSignalRConnection';
import { AccountPage } from 'pages/AccountPage';
import { BillingPage } from 'pages/BillingPage';
import { MyContentLandingPage } from 'pages/ContentLandingPage';
import { DeveloperHubPage } from 'pages/DeveloperHubPage';
import { HomePage } from 'pages/HomePage';
import { NotFoundPage } from 'pages/NotFoundPage';
import { WordLandingPage } from 'pages/WordLandingPage';
import { WorkPathRouting } from 'pages/WorkPathRouting';
import { useEffect, useMemo, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useSelector } from 'react-redux';
import { Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { StorageManagerInstance } from 'storage/StorageManager';
import { selectSubscriptionFriendlyName } from 'store/AccountInfoHelpers';
import { connectToOrganizationSignalR, fetchOrganizationAccount } from 'store/organization/actions';
import { useGetIsFirstArrivalQuery } from 'store/organization/organizationSlice';
import { useGetWorkPathsQuery } from 'store/work_path/workPathSlice';
import { ChameleonTelemetryProvider } from 'telemetry/ChameleonTelemetryProvider';
import { TelemetryContext } from 'telemetry/TelemetryContext';
import { TelemetryEvent, UserbackClosedSchema, UserbackOpenedSchema, UserbackSendingSchema, UserbackSentSchema } from 'telemetry/TelemetryEvents';
import { Telemetry } from 'telemetry/TelemetryManager';
import { ITelemetryProvider } from 'telemetry/TelemetryProvider';
import { registerDocugamiIcons } from '../../../hooks/dgIcons';
import { DocumentPage } from '../../../pages/DocumentPage';
import { AppState } from '../../../store';
import { reportErrorEncountered } from '../../../store/authentication/actions';
import { processUrlHashParameters } from '../../../store/urlhashparameters/actions';
import withOrgAdminRoleRequired from '../utils/AdminSecuredComponent';
import { OfficeOnlineAuthTaskpane } from '../utils/AuthHelpers';
import { ConfirmationDialog } from '../utils/ConfirmationDialog';
import { KeyboardAccelerators } from '../utils/KeyboardAccelerators';
import withSupportedPlatformRequired from '../utils/UnsupportedPlatform';
import withOrganizationRequired from '../utils/WithOrganizationRequired';
import { AppShell } from './AppShell';
import CreateOrganization from './CreateOrganization';
import { ZapierWorkflowPage } from 'pages/ZapierWorkflowPage';
import { useFeatureFlag } from 'hooks/FeatureFlags';
import { FeatureFlags } from 'store/featureFlags/types';
import { PowerAutomateWorkflowPage } from 'pages/PowerAutomateWorkflowPage';

const relativeTime = require('dayjs/plugin/relativeTime');

const chmln = require('@chamaeleonidae/chmln');
const { v4: uuidv4 } = require('uuid');

dayjs.extend(relativeTime);
initializeIcons();
registerDocugamiIcons();

interface AppProps {
    location: any;
}

const anonymousUrls = [`${Config.apiUrl}/ClientConfiguration`, `https://${Config.auth0.domain}/dbconnections/change_password`];

const loginOptions = { loginOptions: { appState: { hash: window.location.hash } } };
const SecuredZapierWorfklowPage = withAuthenticationRequired(withOrganizationRequired(ZapierWorkflowPage), loginOptions);
const SecuredPowerAutomateWorkflowPage = withAuthenticationRequired(withOrganizationRequired(PowerAutomateWorkflowPage), loginOptions);
const SecuredWorkPathRouting = withSupportedPlatformRequired(withAuthenticationRequired(withOrganizationRequired(WorkPathRouting), loginOptions));
const SecuredNewLandingPage = withAuthenticationRequired(withOrganizationRequired(HomePage), loginOptions);
const SecuredAccountPage = withAuthenticationRequired(withOrganizationRequired(AccountPage), loginOptions);
const SecuredBillingPage = withAuthenticationRequired(withOrganizationRequired(withOrgAdminRoleRequired(BillingPage, { redirectIfNotAdmin: true })), loginOptions);
const SecuredDocumentPage = withAuthenticationRequired(withOrganizationRequired(DocumentPage), loginOptions);
const SecuredOrganization = withAuthenticationRequired(CreateOrganization, loginOptions);
const SecuredMyContentLandingPage = withSupportedPlatformRequired(withAuthenticationRequired(withOrganizationRequired(MyContentLandingPage), loginOptions));
const SecuredDeveloperHubPage = withSupportedPlatformRequired(withAuthenticationRequired(withOrganizationRequired(withOrgAdminRoleRequired(DeveloperHubPage, { redirectIfNotAdmin: true })), loginOptions));

export const App = (props: AppProps): JSX.Element => {
    const { error, isLoading, isAuthenticated, user, hasOrgClaims } = useDgAuth();
    useAuthenticateRequests(anonymousUrls);
    useNetworkErrorTracker();

    const errorEncountered = useSelector((state: AppState) => state.authentication.errorEncountered);
    const [prevPath, setPrevPath] = useState('');
    const loginUrl = useSelector((state: AppState) => state.authentication.loginUrl);
    const curOrg = useSelector((state: AppState) => state.organizations.currentOrganization);
    const userEmail = user?.email;
    const officeMode = (window as any)._OfficeMode;

    const navService = useNavigationService();
    const history = useHistory();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const [prevHistoryLen, setPrevHistoryLen] = useState(history.length);
    const { isUploading } = useUploadProgress();
    const { needToCreateAnOrg, currentOrgInitializing } = useOrgStatus();
    const demoEnabled = useFeatureFlag(FeatureFlags.DemoFeatures);

    useGetWorkPathsQuery(!!curOrg?.id && isAuthenticated ? undefined : skipToken);

    const subscriptionTypeFriendlyName = useAppSelector(selectSubscriptionFriendlyName)
    const context = useMemo(() => ({
        subscriptionType: subscriptionTypeFriendlyName
    }), [subscriptionTypeFriendlyName]);

    useTelemetryContext(context);
    useTelemetryUserContext();
    useFileManagerReduxInterop();

    const { data: showFirstArrival } = useGetIsFirstArrivalQuery(!!curOrg?.id && isAuthenticated ? undefined : skipToken);

    useEffect(() => {
        if (showFirstArrival && location.pathname.match(new RegExp(`${MY_CONTENT_PAGE}|${DOC_SHELL_PAGE}|${WORK_PATH_PAGE}`, 'g'))) {
            navService.navToHomePage(NavigationType.PushNewPage);
        }
    }, [location.pathname, navService, showFirstArrival])

    useEffect(() => {
        if (window.location.pathname !== '/callback') {
            dispatch(processUrlHashParameters(window.location.hash, window.location.search));
        }
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [])

    useEffect(() => {
        if (!!error) {
            dispatch(reportErrorEncountered('Auth Error', error));
        }
    }, [error, dispatch])

    useEffect(() => {
        // If the user tries to close the tab while files are uploading, warn them
        if (isUploading) {
            window.onbeforeunload = () => {
                return true;
            };

            return () => {
                window.onbeforeunload = null
            };
        }
    }, [isUploading]);

    useEffect(() => {
        if (history.length < prevHistoryLen) {
            // we just did a back navigation and are still in our SPA
            if (history.location.pathname === prevPath) {
                // we just did a back navigation to a page with the same pathname
                // we ideally don't want that to happen.... so go back again
                history.goBack();
            }
        }

        setPrevHistoryLen(history.length);

        if (prevPath !== window.location.pathname && window.location.pathname !== "/") {
            setPrevPath(window.location.pathname);
        }

    }, [history, prevHistoryLen, prevPath]);

    useEffect(() => {
        // Init userback.io (for user feedback)
        const userback = (window as any).Userback;
        if (!!userback && !!userEmail) {
            userback.init(Config.userbackKey, {
                email: userEmail,
                name: user?.name,
                on_open: () => {
                    userback.userbackId = uuidv4();
                    userback.setData({ ...TelemetryContext.context, userbackId: userback.userbackId });

                    Telemetry.trackEvent<UserbackOpenedSchema>({
                        userbackId: userback.userbackId,
                        eventName: TelemetryEvent.UserbackOpened
                    });
                    KeyboardAccelerators.addOrRemoveDisabler(true, "Userback");
                },
                on_close: () => {
                    Telemetry.trackEvent<UserbackClosedSchema>({
                        userbackId: userback.userbackId,
                        eventName: TelemetryEvent.UserbackClosed
                    });
                    KeyboardAccelerators.addOrRemoveDisabler(false, "Userback");
                },
                before_send: () => {
                    userback.custom_data = TelemetryContext.context;

                    Telemetry.trackEvent<UserbackSendingSchema>({
                        userbackId: userback.userbackId,
                        eventName: TelemetryEvent.UserbackSending
                    });
                },
                after_send: () => {
                    Telemetry.trackEvent<UserbackSentSchema>({
                        userbackId: userback.userbackId,
                        eventName: TelemetryEvent.UserbackSent
                    });
                    KeyboardAccelerators.addOrRemoveDisabler(false, "Userback");
                }
            });
        }
    }, [userEmail, user?.name]);

    useEffect(() => {
        // Init chameleon.io (for tours and tooltips)
        if (!!userEmail && !!Config.chameleonKey && !officeMode) {
            chmln.init(Config.chameleonKey, { fastUrl: 'https://fast.chameleon.io/', forceOverride: true });
            chmln.identify(userEmail, { email: userEmail, name: user?.name })

            const chameleonTelemetryProvider: ITelemetryProvider = new ChameleonTelemetryProvider();
            Telemetry.register(chameleonTelemetryProvider)
        }
    }, [userEmail, user?.name, officeMode]);

    function navToHome(): void {
        dispatch(reportErrorEncountered('', null));
        if (loginUrl) {
            console.log(`redirecting to login page ${loginUrl}`);
            window.location.href = loginUrl;
        }
    }

    // Telemetry: initialize for web app
    useEffect(() => {
        if (!isLoading && isAuthenticated && !!userEmail && !!curOrg?.id) {
            Telemetry.setUserContext(userEmail, curOrg.id);
        }
    }, [userEmail, isLoading, isAuthenticated, curOrg])

    const isSignalRReady = useMemo(() => !!curOrg?.id && isAuthenticated, [curOrg?.id, isAuthenticated]);
    useEffect(() => {
        if (isSignalRReady) {
            dispatch(connectToOrganizationSignalR());
        }

        return () => stopSignalRConnection();
    }, [isSignalRReady, dispatch])

    useEffect(() => {
        if (!!curOrg?.id) {
            StorageManagerInstance.setOrgId(curOrg.id);
        }
    }, [curOrg])

    useEffect(() => {
        if (!!curOrg?.id) {
            dispatch(fetchOrganizationAccount());
        }
    }, [curOrg, dispatch])

    useEffect(() => {
        if ((hasOrgClaims === false || needToCreateAnOrg || currentOrgInitializing) && window.location.pathname !== '/organization') {
            navService.navToOrganizationPage();
        }
    }, [hasOrgClaims, navService, currentOrgInitializing, needToCreateAnOrg])

    return (
        <div>
            <DndProvider backend={HTML5Backend}>
                <AppShell>
                    <Switch>
                        <Route path={['/home', '/understand', '/create', '/workflow']} component={SecuredNewLandingPage} location={props.location} />
                        <Route path={['/dg-prod-app', '/dg-staging-app']} component={SecuredNewLandingPage} location={props.location} exact={true} />
                        <Route path={'/login'} component={SecuredNewLandingPage} location={props.location} />

                        <Route path={'/'} component={officeMode ? OfficeOnlineAuthTaskpane : SecuredNewLandingPage} location={props.location} exact={true} />

                        <Route path={['/my-content/:targetId?']} component={SecuredMyContentLandingPage} location={props.location} />
                        <Route path='/organization' component={SecuredOrganization} location={props.location} />

                        <Route path={['/document-shell/cluster/:clusterid/folder/:folderid/instance/:instanceid',
                            '/document-shell/cluster/:clusterid/folder/:folderid']}
                            component={SecuredDocumentPage}
                            location={props.location} />

                        {
                            demoEnabled &&
                            <Route path={[
                                '/zapier-workflow']}
                                component={SecuredZapierWorfklowPage}
                                location={props.location} />
                        }

                        {
                            demoEnabled &&
                            <Route path={[
                                '/power-automate-workflow']}
                                component={SecuredPowerAutomateWorkflowPage}
                                location={props.location} />
                        }

                        <Route path={[
                            '/workPath']}
                            component={SecuredWorkPathRouting}
                            location={props.location} />

                        <Route path={'/account'} component={SecuredAccountPage} location={props.location} />
                        <Route path={'/tiers'} component={SecuredBillingPage} location={props.location} />
                        <Route path={`/${DEVELOPER_HUB_PAGE}/:target?`} component={SecuredDeveloperHubPage} location={props.location} />

                        <Route path={'/word/taskpane'} component={WordLandingPage} location={props.location} />
                        <Route component={NotFoundPage} location={props.location} />
                    </Switch>
                </AppShell>
            </DndProvider>
            <ConfirmationDialog
                hidden={!errorEncountered}
                title="Oops! Something went wrong!"
                subText="We will look into it right away. Please check back again later."
                confirmButtonText="OK"
                dismissButtonText=""
                confirmedCallback={navToHome}
                dismissedCallback={navToHome} />
            <UpgradeAccountModal />
        </div>
    );
}

export default App;
